import { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { lazyWithRetry } from '@/utils/lazyWithRetry';

// Lazy-loaded professional pages with retry for network reliability
const ProfessionalCommandCenter = lazyWithRetry(() => import('@/pages/ProfessionalCommandCenter'));
const MonitorPortal = lazyWithRetry(() => import('@/pages/MonitorPortal'));
const CRORecruiter = lazyWithRetry(() => import('@/pages/CRORecruiter'));
const CROCommandCenter = lazyWithRetry(() => import('@/pages/CROCommandCenter'));
const SponsorCommandCenter = lazyWithRetry(() => import('@/pages/SponsorCommandCenter'));
const SponsorRegistration = lazyWithRetry(() => import('@/pages/SponsorRegistration'));
const TrialAnalytics = lazyWithRetry(() => import('@/pages/TrialAnalytics'));

// CRO Assessment pages
const CROAssessment = lazyWithRetry(() => import('@/pages/CROAssessment'));
const CROAssessmentResults = lazyWithRetry(() => import('@/pages/CROAssessmentResults'));
const CROTrainingVideos = lazyWithRetry(() => import('@/pages/cro/CROTrainingVideos'));
const CROStaffPortal = lazyWithRetry(() => import('@/pages/cro/CROStaffPortal'));
const MyCredentials = lazyWithRetry(() => import('@/pages/MyCredentials'));
const SponsorTrainingVideos = lazyWithRetry(() => import('@/pages/sponsor/SponsorTrainingVideos'));
const CRODirectory = lazyWithRetry(() => import('@/pages/CRODirectory'));
const EthicsSubmissionsRegister = lazyWithRetry(() => import('@/pages/EthicsSubmissionsRegister'));

// Advanced features (from LazyRoutes)
import {
  ProfessionalAnalyticsLazy,
  DecentralizedTrialsLazy,
  RegulatoryComplianceLazy,
} from '@/components/layout/LazyRoutes';

// Dashboard components
const TokenEconomyDashboard = lazyWithRetry(() => import('@/pages/dashboard/TokenEconomyDashboard'));
const IntegrationHubDashboard = lazyWithRetry(() => import('@/pages/dashboard/IntegrationHubDashboard'));
const DecentralizedTrialsDashboard = lazyWithRetry(() => import('@/pages/dashboard/DecentralizedTrialsDashboard'));

// Standalone feature pages
const BehavioralAnalyzer = lazyWithRetry(() => import('@/pages/BehavioralAnalyzer'));
const IntegrationHub = lazyWithRetry(() => import('@/pages/IntegrationHub'));
const TokenEconomy = lazyWithRetry(() => import('@/pages/TokenEconomy'));
const ComplianceDashboard = lazyWithRetry(() => import('@/pages/ComplianceDashboard'));

// Facility Portal (shared-device data entry for labs, clinicians, surveillance, campaigns)
const FacilityPortal = lazyWithRetry(() => import('@/pages/FacilityPortal'));
const FacilityOnboarding = lazyWithRetry(() => import('@/pages/FacilityOnboarding'));
const FacilitySettings = lazyWithRetry(() => import('@/pages/FacilitySettings'));



/**
 * Professional routes for CROs, Sponsors, and Medical Professionals
 */
export const professionalRoutes: RouteObject[] = [
  // Registration & onboarding
  { path: '/sponsor-registration', element: <SponsorRegistration /> },
  { path: '/monitor', element: <ProtectedRoute><MonitorPortal /></ProtectedRoute> },
  
  // Command Centers by role
  { 
    path: '/professional', 
    element: <ProtectedRoute requiredRole="medical_professional"><ProfessionalCommandCenter /></ProtectedRoute> 
  },
  { 
    path: '/cro/command-center', 
    element: <ProtectedRoute requiredRole="cro"><CROCommandCenter /></ProtectedRoute> 
  },
  { 
    path: '/sponsor/command-center', 
    element: <ProtectedRoute requiredRole="sponsor"><SponsorCommandCenter /></ProtectedRoute> 
  },
  
  // CRO Assessment routes
  { path: '/cro-assessment', element: <ProtectedRoute><CROAssessment /></ProtectedRoute> },
  { path: '/ethics-submissions', element: <ProtectedRoute><EthicsSubmissionsRegister /></ProtectedRoute> },
  { path: '/cro-assessment-results', element: <ProtectedRoute><CROAssessmentResults /></ProtectedRoute> },
  
  // CRO staff's own workspace (their studies, visits and documents)
  // CRO recruiter / field worker (role checked server-side in cro-recruiter-sync and by RLS)
  { path: '/cro/recruiter', element: <ProtectedRoute><CRORecruiter /></ProtectedRoute> },
  { path: '/cro/portal', element: <ProtectedRoute><CROStaffPortal /></ProtectedRoute> },
  // Site staff upload their own CV / GCP / training (approval required)
  { path: '/staff/my-credentials', element: <ProtectedRoute><MyCredentials /></ProtectedRoute> },

  // CRO Training Videos
  { path: '/cro/training-videos', element: <ProtectedRoute requiredRole="cro"><CROTrainingVideos /></ProtectedRoute> },
  
  // Sponsor Training Videos
  { path: '/sponsor/training-videos', element: <ProtectedRoute requiredRole="sponsor"><SponsorTrainingVideos /></ProtectedRoute> },
  { path: '/cro-directory', element: <CRODirectory /> },
  
  // Analytics
  { path: '/trial-analytics/:trialId', element: <TrialAnalytics /> },
  { path: '/professional-analytics', element: <ProfessionalAnalyticsLazy /> },
  
  // Advanced features
  { path: '/decentralized-trials', element: <DecentralizedTrialsLazy /> },
  { path: '/regulatory-compliance', element: <RegulatoryComplianceLazy /> },
  
  // Dashboard features
  { path: '/dashboard/token-economy', element: <TokenEconomyDashboard /> },
  { path: '/dashboard/integration-hub', element: <IntegrationHubDashboard /> },
  { path: '/dashboard/decentralized-trials', element: <DecentralizedTrialsDashboard /> },
  
  // Standalone feature pages
  { path: '/behavioral-analyzer', element: <BehavioralAnalyzer /> },
  { path: '/integration-hub', element: <IntegrationHub /> },
  { path: '/token-economy', element: <TokenEconomy /> },
  { path: '/compliance-dashboard', element: <ComplianceDashboard /> },

  // Facility Portal — shared-device entry for labs, clinicians, surveillance, campaigns
  { path: '/facility', element: <ProtectedRoute><FacilityPortal /></ProtectedRoute> },
  { path: '/facility/onboarding', element: <ProtectedRoute><FacilityOnboarding /></ProtectedRoute> },
  { path: '/facility/settings', element: <ProtectedRoute><FacilitySettings /></ProtectedRoute> },
];


// Export lazy components for prefetching
export const professionalRoutePrefetch = {
  ProfessionalCommandCenter,
  CROCommandCenter,
  SponsorCommandCenter,
  TrialAnalytics,
};
